import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { useNavigate } from "react-router-dom";

interface Product {
  id: string;
  category: string;
  name: string;
  material: string;
  price: string;
  description: string;
  image: string;
  marca: string;
}

export const Gioielleria = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [sortBy, setSortBy] = useState("default");

  const navigate = useNavigate();

  const SPREADSHEET_ID = "1P6aNYWLOmysvMxCGomJX3sHD630bIEIbRdrVK0kj42g";
  const YOUR_API_KEY = "AIzaSyDApd2IAji0C8D7cTpv8phMYiqDShinc5c";
  const SHEET_NAME = "Strippoli";

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setErrorMessage(null);
        console.log("Caricamento prodotti Gioielleria...");

        const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/${SHEET_NAME}?key=${YOUR_API_KEY}`;
        console.log("URL della richiesta:", url);

        const response = await axios.get(url);
        const rows = response.data.values;

        console.log("Dati ricevuti:", rows);

        if (!rows || rows.length <= 1) {
          throw new Error("Nessun dato trovato nel foglio.");
        }

        const loadedProducts = rows.slice(1).map((row: string[]) => {
          const convertedImage = row[6]
            ?.replace("/file/d/", "/uc?export=view&id=")
            .replace("/view?usp=drive_link", "");
          console.log("Converted Image URL:", convertedImage);

          return {
            id: row[0],
            category: row[1],
            name: row[2],
            material: row[3],
            price: row[4],
            description: row[5],
            image: convertedImage || "",
            marca: row[7],
          };
        });

        // Filtra solo i prodotti della categoria Gioielleria
        const gioielleriaProducts = loadedProducts.filter(
          (product: { category: string }) =>
            product.category.toLowerCase() === "gioielleria"
        );

        setProducts(gioielleriaProducts);
        setFilteredProducts(gioielleriaProducts);
        console.log("Prodotti Gioielleria:", gioielleriaProducts);
      } catch (err: any) {
        let errorDetail = "Errore sconosciuto.";
        if (err.response) {
          errorDetail = `Errore API: ${err.response.data.error.message}`;
        } else if (err.request) {
          errorDetail = "Impossibile connettersi all'API di Google Sheets.";
        } else {
          errorDetail = err.message || "Errore imprevisto.";
        }
        setErrorMessage(errorDetail);
        console.error("Errore durante il caricamento:", errorDetail);
      }
    };

    fetchProducts();
  }, []);

  // Filter and sort products
  React.useEffect(() => {
    let result = [...products];

    // Search filter
    if (searchTerm) {
      result = result.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.material.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Brand filter
    if (selectedBrand !== "all") {
      result = result.filter((product) => product.marca === selectedBrand);
    }

    // Sort
    switch (sortBy) {
      case "name-asc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "price-asc":
        result.sort((a, b) => {
          const priceA = parseFloat(a.price.replace(/[^0-9.-]+/g, "")) || 0;
          const priceB = parseFloat(b.price.replace(/[^0-9.-]+/g, "")) || 0;
          return priceA - priceB;
        });
        break;
      case "price-desc":
        result.sort((a, b) => {
          const priceA = parseFloat(a.price.replace(/[^0-9.-]+/g, "")) || 0;
          const priceB = parseFloat(b.price.replace(/[^0-9.-]+/g, "")) || 0;
          return priceB - priceA;
        });
        break;
    }

    setFilteredProducts(result);
  }, [products, searchTerm, selectedBrand, sortBy]);

  // Get unique brands from products
  const uniqueBrands = Array.from(new Set(products.map((p) => p.marca))).sort();

  return (
    <>
      <Navbar />
      <main className="flex flex-col items-center bg-gradient-to-b from-gray-50 to-white w-full font-elegant min-h-screen">
        {/* Hero Banner */}
        <div className="w-full relative h-[180px] md:h-[250px] lg:h-[320px] overflow-hidden">
          <img
            src="../img/Missitaliabanner.png"
            alt="Banner collezione gioielleria"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg">
                Gioielleria
              </h1>
              <p className="text-sm md:text-base text-white/90 mt-2 drop-shadow-md">
                Scopri la nostra collezione di gioielli esclusivi
              </p>
            </div>
          </div>
        </div>

        {errorMessage ? (
          <p className="text-red-500 text-center mt-12" role="alert">{errorMessage}</p>
        ) : (
          <div className="w-full max-w-7xl px-3 sm:px-4 md:px-6 py-6 md:py-12">
            {/* Filters and Search Section */}
            <div className="mb-6 md:mb-8 space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Cerca per nome, marca o materiale..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent text-sm sm:text-base"
                  aria-label="Cerca prodotti"
                />
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Cancella ricerca"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Filters Row */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Brand Filter */}
                <div className="flex-1">
                  <label htmlFor="brand-filter" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Marca
                  </label>
                  <select
                    id="brand-filter"
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent text-sm sm:text-base"
                  >
                    <option value="all">Tutte le marche</option>
                    {uniqueBrands.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort */}
                <div className="flex-1">
                  <label htmlFor="sort-filter" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Ordina per
                  </label>
                  <select
                    id="sort-filter"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent text-sm sm:text-base"
                  >
                    <option value="default">Predefinito</option>
                    <option value="name-asc">Nome: A-Z</option>
                    <option value="name-desc">Nome: Z-A</option>
                    <option value="price-asc">Prezzo: Basso-Alto</option>
                    <option value="price-desc">Prezzo: Alto-Basso</option>
                  </select>
                </div>
              </div>

              {/* Results Count */}
              <div className="flex items-center justify-between text-sm text-gray-600">
                <p>
                  Trovati <span className="font-semibold text-green-800">{filteredProducts.length}</span> prodotti
                  {searchTerm && <span> per "{searchTerm}"</span>}
                </p>
                {(searchTerm || selectedBrand !== "all" || sortBy !== "default") && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedBrand("all");
                      setSortBy("default");
                    }}
                    className="text-green-800 hover:text-green-900 font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Resetta filtri
                  </button>
                )}
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3 md:gap-6">
              {filteredProducts.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-600 text-lg mb-2">Nessun prodotto trovato</p>
                  <p className="text-gray-500 text-sm">Prova a modificare i filtri di ricerca</p>
                </div>
              ) : (
                filteredProducts.map((product) => (
                <button
                  key={product.id}
                  className="group bg-white rounded-lg md:rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2 focus:outline-none focus:ring-4 focus:ring-green-800"
                  onClick={() =>
                    navigate("/prodotti-gioielleria", { state: product })
                  }
                  aria-label={`Vedi dettagli ${product.name} ${product.marca}, ${product.material}, prezzo ${product.price}`}
                >
                  <div className="aspect-square relative overflow-hidden bg-gray-100">
                    <img
                      src={product.image}
                      alt={`${product.name} - ${product.material}`}
                      onError={(e) => {
                        console.error(
                          `Errore nel caricamento immagine: ${product.image}`
                        );
                      }}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-2 sm:p-3 md:p-4">
                    <h2 className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-gray-800 mb-0.5 sm:mb-1 line-clamp-2 text-left leading-tight">
                      {product.name}
                    </h2>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mb-1 sm:mb-2 text-left">
                      {product.marca}
                    </p>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-1 text-left">
                      {product.material}
                    </p>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-green-800 text-left">
                      {product.price}
                    </p>
                  </div>
                </button>
              ))
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};
